import { useEffect, useMemo, useState } from 'react';
import { LoadMore, ListEmpty, sortBy } from '@/components/lists';
import type { ArClaim } from '../types';
import { money } from '../utils';

interface Props {
  /** The full "within the payer's usual time" queue (already payer/age filtered upstream). */
  rows: ArClaim[];
  /** The page search (already applied to `rows`); non-empty flattens the list into matches. */
  query: string;
  /** Payers a link filtered to — their groups start open. */
  focusPayers: string[];
  selectedId?: string;
  onSelect: (id: string) => void;
}

function Row({ claim, selected, onSelect }: { claim: ArClaim; selected: boolean; onSelect: (id: string) => void }) {
  return (
    <button type="button" className={`iar-row${selected ? ' on' : ''}`} aria-current={selected || undefined} onClick={() => { onSelect(claim.id); }}>
      <span className="iar-rtop">
        <strong className="iar-rname">{claim.patient}</strong>
        <span className="iar-ramt num">{money(claim.billed)}</span>
      </span>
      <span className="iar-rpayer num">{claim.id} · member {claim.memberId}</span>
      <span className="iar-rmeta">{claim.rowLine} · {claim.rowMeta}</span>
    </button>
  );
}

const PAGE = 50;

/**
 * "Within the payer's usual time" (00-ds-09 §9): a big queue (150–900 open)
 * presented COLLAPSED BY PAYER with per-payer counts, each payer loading 50 at a
 * time; plus its own search (claim number, member ID, amount) that flattens the
 * list. UI-only over mock data — real search/paging is server-side (§8).
 */
export function WithinUsual({ rows, query, focusPayers, selectedId, onSelect }: Props) {

  // collapsed-by-payer state
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [groupLoaded, setGroupLoaded] = useState<Record<string, number>>({});
  // flat (searching) state
  const [flatLoaded, setFlatLoaded] = useState(PAGE);

  const searching = query.trim() !== '';

  const flat = useMemo(() => sortBy(rows, (c) => c.days, 1), [rows]);

  const focusKey = focusPayers.join('|');
  useEffect(() => {
    if (focusPayers.length === 0) return;
    setExpanded((prev) => new Set([...prev, ...focusPayers]));
    setGroupLoaded((gl) => {
      const next = { ...gl };
      for (const p of focusPayers) next[p] ??= PAGE;
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey]);

  const groups = useMemo(() => {
    const byPayer = new Map<string, ArClaim[]>();
    for (const c of rows) {
      const list = byPayer.get(c.payer.name) ?? [];
      list.push(c);
      byPayer.set(c.payer.name, list);
    }
    return [...byPayer.entries()]
      .map(([payer, items]) => ({
        payer,
        items: sortBy(items, (c) => c.days, 1),
        total: items.reduce((s, c) => s + c.balance, 0),
      }))
      .sort((a, b) => b.items.length - a.items.length);
  }, [rows]);

  const toggle = (payer: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(payer)) next.delete(payer);
      else {
        next.add(payer);
        setGroupLoaded((gl) => (gl[payer] ? gl : { ...gl, [payer]: PAGE }));
      }
      return next;
    });
  };

  return (
    <section className="iar-wu" aria-label="Claims within the payer’s usual time">
      {searching ? (
        flat.length === 0 ? (
          <ListEmpty query={query.trim()} message={`Nothing in this queue matches “${query.trim()}”.`} />
        ) : (
          <>
            <h3 className="iar-grp">{flat.length} {flat.length === 1 ? 'match' : 'matches'}</h3>
            {flat.slice(0, flatLoaded).map((c) => (
              <Row key={c.id} claim={c} selected={selectedId === c.id} onSelect={onSelect} />
            ))}
            <LoadMore loaded={Math.min(flatLoaded, flat.length)} total={flat.length} onMore={() => { setFlatLoaded((n) => n + PAGE); }} />
          </>
        )
      ) : (
        groups.map((g) => {
          const open = expanded.has(g.payer);
          const loaded = groupLoaded[g.payer] ?? PAGE;
          return (
            <div key={g.payer} className="iar-wu-group">
              <button
                type="button"
                className="iar-wu-head"
                aria-expanded={open}
                onClick={() => { toggle(g.payer); }}
              >
                <span className={`iar-wu-caret${open ? ' open' : ''}`} aria-hidden>▸</span>
                <span className="iar-wu-payer">{g.payer}</span>
                <span className="iar-wu-count">{g.items.length}</span>
                <span className="iar-wu-total num">{money(g.total)}</span>
              </button>
              {open ? (
                <>
                  {g.items.slice(0, loaded).map((c) => (
                    <Row key={c.id} claim={c} selected={selectedId === c.id} onSelect={onSelect} />
                  ))}
                  <LoadMore
                    loaded={Math.min(loaded, g.items.length)}
                    total={g.items.length}
                    onMore={() => { setGroupLoaded((gl) => ({ ...gl, [g.payer]: (gl[g.payer] ?? PAGE) + PAGE })); }}
                  />
                </>
              ) : null}
            </div>
          );
        })
      )}
    </section>
  );
}
